import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { X, ArrowLeft, Menu, Wallet, Search, ChevronDown, Printer, CreditCard, Smartphone, HelpCircle, Banknote, BookUser } from 'lucide-react';
import DataService from '../services/dataService';
import LogoLoader from '../components/LogoLoader';
import PrinterSettings from '../components/PrinterSettings';
import thermalPrinter from '../services/thermalPrinterService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Payment method options
const PAYMENT_METHODS = [
  { id: 'cash', name: 'Cash', icon: Banknote, color: 'green', description: 'Direct cash payment' },
  { id: 'digital', name: 'Digital', icon: Smartphone, color: 'blue', description: 'UPI, PayTM, GPay, etc.' },
  { id: 'card', name: 'Card', icon: CreditCard, color: 'purple', description: 'Debit/Credit Card' },
  { id: 'other', name: 'Other', icon: HelpCircle, color: 'gray', description: 'Custom payment method' },
];

const GenerateOrder = ({ user }) => {
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showCategories, setShowCategories] = useState(false);
  const [parties, setParties] = useState([]);
  
  // Unified payment state
  const [selectedLedger, setSelectedLedger] = useState('cash');
  const [selectedLedgerName, setSelectedLedgerName] = useState('Cash');
  const [selectedPartyId, setSelectedPartyId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentMethodOther, setPaymentMethodOther] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [partySearchQuery, setPartySearchQuery] = useState('');
  
  // Printer related states
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [printerSettings, setPrinterSettings] = useState(null);
  const saveDropdownRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      await Promise.all([fetchMenuItems(), fetchParties(), fetchPrinterSettings()]);
      setInitialLoading(false);
    };
    loadData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (saveDropdownRef.current && !saveDropdownRef.current.contains(event.target)) {
        setShowSaveDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPrinterSettings = async () => {
    try {
      const response = await axios.get(`${API}/printer/settings/${user.franchise.id}`);
      if (response.data.settings) {
        setPrinterSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching printer settings:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const items = await DataService.getMenuItems(user.franchise.id);
      
      const sortedItems = [...items].sort((a, b) => {
        const categoryCompare = (a.category || '').localeCompare(b.category || '');
        if (categoryCompare !== 0) return categoryCompare;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setMenuItems(sortedItems);
      
      const uniqueCategories = [...new Set(sortedItems.map(item => item.category))].sort();
      setCategories(['All', ...uniqueCategories]);
      
      if (!navigator.onLine && items.length > 0) {
        toast.info('Using cached menu items (offline)', { duration: 3000 });
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to load menu items');
    }
  };

  const fetchParties = async () => {
    try {
      const partiesData = await DataService.getLedgerParties(user.franchise.id);
      setParties(partiesData);
      
      if (!navigator.onLine && partiesData.length > 0) {
        console.log('[GenerateOrder] Using cached ledger parties (offline)');
      }
    } catch (error) {
      console.error('Error fetching parties:', error);
    }
  };

  const filteredItems = menuItems
    .filter(item => selectedCategory === 'All' || item.category === selectedCategory)
    .filter(item => 
      searchQuery === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const audioContextRef = React.useRef(null);
  
  React.useEffect(() => {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    } catch (error) {
      console.log('Audio not supported');
    }
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playTapSound = () => {
    try {
      const audioContext = audioContextRef.current;
      if (!audioContext) return;
      
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.log('Tap sound failed:', error);
    }
  };

  const handleAddItem = (item) => {
    playTapSound();
    setCart(prev => ({
      ...prev,
      [item.id]: {
        ...item,
        quantity: (prev[item.id]?.quantity || 0) + 1
      }
    }));
  };

  const handleRemoveItem = (itemId) => {
    playTapSound();
    setCart(prev => {
      const newCart = { ...prev };
      delete newCart[itemId];
      return newCart;
    });
  };

  const calculateTotal = () => {
    return Object.values(cart).reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
  };

  // Unified payment selection handler
  const handleSelectPayment = (methodId, partyData = null) => {
    setPaymentMethod(methodId);
    
    if (partyData) {
      // Ledger party selected
      const ledgerValue = `${partyData.party_name}-${partyData.party_id}`;
      setSelectedLedger(ledgerValue);
      setSelectedLedgerName(partyData.party_name);
      setSelectedPartyId(partyData.party_id);
    } else {
      // Regular payment method (cash, digital, card)
      setSelectedLedger('cash');
      setSelectedLedgerName(PAYMENT_METHODS.find(m => m.id === methodId)?.name || 'Cash');
      setSelectedPartyId(null);
    }
    
    if (methodId !== 'other') {
      setPaymentMethodOther('');
      setShowPaymentModal(false);
    }
  };

  const handleConfirmOtherPayment = () => {
    if (paymentMethodOther.trim()) {
      setSelectedLedgerName(paymentMethodOther);
      setShowPaymentModal(false);
    } else {
      toast.error('Please enter a payment method name');
    }
  };

  const getPaymentDisplay = () => {
    if (selectedPartyId) {
      return selectedLedgerName; // Party name for ledger
    }
    if (paymentMethod === 'other' && paymentMethodOther) {
      return paymentMethodOther;
    }
    return PAYMENT_METHODS.find(m => m.id === paymentMethod)?.name || 'Cash';
  };

  const getPaymentIcon = () => {
    if (selectedPartyId) {
      return BookUser; // Ledger icon
    }
    return PAYMENT_METHODS.find(m => m.id === paymentMethod)?.icon || Banknote;
  };

  const handleSaveOrder = async (shouldPrint = false) => {
    if (Object.keys(cart).length === 0) {
      toast.error('Please add at least one item to the order');
      return;
    }

    // Validate "Other" payment method
    if (paymentMethod === 'other' && !paymentMethodOther.trim() && !selectedPartyId) {
      toast.error('Please specify the payment method');
      setShowPaymentModal(true);
      return;
    }

    setLoading(true);
    setShowSaveDropdown(false);
    
    try {
      const orderTimestamp = new Date().toISOString();
      
      const orderItems = Object.values(cart).map(item => {
        const menuItem = menuItems.find(m => m.id === item.id);
        return {
          menu_item_id: item.id,
          item_name_snapshot: item.name,
          item_price_snapshot: parseFloat(item.price),
          quantity: item.quantity,
          category: menuItem?.category || 'Uncategorized'
        };
      });

      const totalAmount = orderItems.reduce((sum, item) => 
        sum + (item.item_price_snapshot * item.quantity), 0
      );

      const orderData = {
        franchise_id: user.franchise.id,
        items: orderItems,
        total_amount: totalAmount,
        ledger: selectedPartyId ? selectedLedger : 'cash',
        ledger_party_id: selectedPartyId,
        ledger_name: selectedPartyId ? selectedLedgerName : null,
        date_timestamp: orderTimestamp,
        payment_method: paymentMethod,
        payment_method_other: paymentMethod === 'other' ? paymentMethodOther : null
      };

      let savedOrderNo = null;

      if (navigator.onLine) {
        const response = await axios.post(`${API}/orders`, orderData);
        savedOrderNo = response.data.order_no;
        toast.success('Invoice saved successfully!');
      } else {
        await DataService.saveOfflineOrder(orderData);
        toast.success('Invoice saved offline! Will sync when online.', { duration: 4000 });
      }
      
      // Print receipt if requested
      if (shouldPrint && savedOrderNo) {
        await handlePrintReceipt(savedOrderNo);
      }
      
      // Reset form
      setCart({});
      setSelectedLedger('cash');
      setSelectedLedgerName('Cash');
      setSelectedPartyId(null);
      setPaymentMethod('cash');
      setPaymentMethodOther('');
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = async (orderNo) => {
    try {
      const response = await axios.get(`${API}/receipt/data/${orderNo}`);
      const receiptData = response.data;
      await thermalPrinter.printReceipt(receiptData, printerSettings || receiptData.settings);
      toast.success('Receipt sent to printer!');
    } catch (error) {
      console.error('Error printing receipt:', error);
      toast.error('Failed to print receipt');
    }
  };

  const totalAmount = calculateTotal();
  const cartItemsCount = Object.values(cart).reduce((sum, item) => sum + item.quantity, 0);
  const PaymentIcon = getPaymentIcon();

  const colorClasses = {
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    gray: 'bg-gray-100 text-gray-600',
    amber: 'bg-amber-100 text-amber-600',
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Initial Loading Screen */}
      {initialLoading && (
        <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
          <LogoLoader size="lg" text="Loading Menu..." />
        </div>
      )}
      
      {/* Saving Order Loader */}
      {loading && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <LogoLoader size="md" text="Saving Order..." />
        </div>
      )}
      
      {/* Header */}
      <div className="sticky top-0 z-40 bg-surface border-b border-border shadow-sm">
        <div className="flex items-center justify-between p-2 sm:p-3 md:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 sm:h-10 sm:w-10 p-0 rounded-full"
              onClick={() => navigate('/franchise')}
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <div>
              <h1 className="text-base sm:text-lg md:text-xl font-bold text-primary">Generate Order</h1>
              <p className="text-[10px] sm:text-xs text-secondary truncate max-w-[120px] sm:max-w-none">{user.franchise.name}</p>
            </div>
          </div>
          
          {/* Mobile Category Toggle */}
          <Button
            variant="outline"
            size="sm"
            className="lg:hidden h-9 sm:h-10 px-2 sm:px-3 rounded-full"
            onClick={() => setShowCategories(!showCategories)}
          >
            <Menu className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
            <span className="hidden sm:inline text-sm">Categories</span>
          </Button>
        </div>

        {/* Mobile Category Dropdown */}
        {showCategories && (
          <div className="lg:hidden border-t border-border bg-muted">
            <div className="flex overflow-x-auto gap-2 p-2 sm:p-3 scrollbar-hide">
              {categories.map((category) => (
                <Button
                  key={category}
                  data-testid={`category-${category}-button`}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  className="whitespace-nowrap rounded-full font-bold text-xs sm:text-sm px-3 h-8"
                  onClick={() => {
                    setSelectedCategory(category);
                    setShowCategories(false);
                  }}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Categories Sidebar - Desktop Only */}
        <div className="hidden lg:block w-36 xl:w-40 bg-surface border-r border-border overflow-y-auto flex-shrink-0">
          <div className="p-3 xl:p-4 space-y-2">
            {categories.map((category) => (
              <Button
                key={category}
                data-testid={`category-${category}-button`}
                variant={selectedCategory === category ? 'default' : 'ghost'}
                className="w-full h-auto min-h-[2.5rem] py-2 px-2 xl:px-3 rounded-xl font-bold text-xs xl:text-sm uppercase tracking-wider flex items-center justify-center"
                onClick={() => setSelectedCategory(category)}
              >
                <span className="text-center break-words leading-tight">{category}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Menu Items Grid */}
        <div className="flex-1 overflow-y-auto pb-28 sm:pb-32">
          <div className="p-2 sm:p-4 md:p-6">
            {/* Search Bar */}
            <div className="mb-3 sm:mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search menu items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 sm:pl-10 h-10 sm:h-12 rounded-full border-2 focus:border-accent text-sm sm:text-base"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-xs sm:text-sm text-secondary mt-2">
                  Found {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} matching "{searchQuery}"
                </p>
              )}
            </div>
            
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-primary mb-3 sm:mb-4">
              {selectedCategory === 'All' ? 'All Items' : selectedCategory}
            </h2>
            {filteredItems.length === 0 ? (
              <div className="col-span-full text-center py-8 sm:py-12">
                <Search className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-muted mb-3 sm:mb-4" />
                <p className="text-base sm:text-lg text-secondary mb-2">No items found</p>
                {searchQuery && (
                  <p className="text-xs sm:text-sm text-secondary">
                    Try a different search term or{' '}
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="text-accent hover:underline font-medium"
                    >
                      clear search
                    </button>
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Group items by category */}
                {(() => {
                  const sortedItems = [...filteredItems].sort((a, b) => {
                    const categoryCompare = (a.category || '').localeCompare(b.category || '');
                    if (categoryCompare !== 0) return categoryCompare;
                    return (a.name || '').localeCompare(b.name || '');
                  });
                  
                  const grouped = sortedItems.reduce((acc, item) => {
                    const cat = item.category || 'Uncategorized';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(item);
                    return acc;
                  }, {});
                  
                  const sortedCategories = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
                  
                  return sortedCategories.map((category) => (
                    <div key={category}>
                      {/* Category Header/Divider */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-[1px] bg-accent/30 flex-grow"></div>
                        <span className="text-[10px] sm:text-xs font-bold text-accent uppercase tracking-wider px-2 whitespace-nowrap">{category}</span>
                        <div className="h-[1px] bg-accent/30 flex-grow"></div>
                      </div>
                      
                      {/* Items Grid */}
                      <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12 gap-1 sm:gap-1.5">
                        {grouped[category].map((item) => (
                          <Card
                            key={item.id}
                            data-testid={`menu-item-${item.id}`}
                            className={`menu-item-card shadow-sm border-[0.5px] cursor-pointer transition-all hover:shadow-md relative rounded-sm ${
                              cart[item.id] ? 'border-accent border-2 bg-accent/5' : 'border-gray-300 hover:border-accent hover:border-2'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddItem(item);
                            }}
                          >
                            <CardContent className="p-1 sm:p-1.5 flex flex-col justify-between h-full min-h-[45px] sm:min-h-[50px] rounded-none">
                              {cart[item.id] && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveItem(item.id);
                                  }}
                                  className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md z-10"
                                  title="Remove from cart"
                                >
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              )}

                              <div>
                                <h3 className="font-bold text-[10px] sm:text-xs md:text-sm text-primary leading-tight line-clamp-2">{item.name}</h3>
                              </div>
                              <div className="flex justify-between items-center mt-0.5">
                                <p className="text-[9px] sm:text-xs font-bold text-accent">₹{item.price}</p>
                                {cart[item.id] && (
                                  <div className="flex items-center justify-center bg-accent text-white rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4">
                                    <span className="text-[7px] sm:text-[8px] font-bold">{cart[item.id].quantity}</span>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Footer - Simplified with single payment button */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border shadow-lg z-50">
        <div className="flex items-center justify-between p-2 sm:p-3 md:p-4 gap-2 sm:gap-3">
          {/* Total Amount */}
          <div className="flex flex-col min-w-0">
            <span className="text-[9px] sm:text-xs text-secondary">Total ({cartItemsCount})</span>
            <span className="text-lg sm:text-xl md:text-2xl font-bold text-accent truncate">₹{totalAmount.toFixed(2)}</span>
          </div>

          {/* Unified Payment Method Button */}
          <Button
            type="button"
            variant="outline"
            data-testid="payment-button"
            onClick={() => setShowPaymentModal(true)}
            className={`h-10 sm:h-11 md:h-12 px-3 sm:px-4 rounded-xl flex items-center gap-2 min-w-[100px] sm:min-w-[140px] ${
              selectedPartyId ? 'border-amber-400 bg-amber-50' : ''
            }`}
          >
            <PaymentIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="font-medium text-xs sm:text-sm truncate">{getPaymentDisplay()}</span>
            {selectedPartyId && <span className="text-[9px] sm:text-xs text-amber-600 font-medium">(Ledger)</span>}
          </Button>

          {/* Printer Settings Button */}
          <Button
            type="button"
            variant="ghost"
            data-testid="printer-settings-button"
            onClick={() => setShowPrinterSettings(true)}
            className="h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12 rounded-xl flex items-center justify-center flex-shrink-0"
            title="Printer Settings"
          >
            <Printer className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>

          {/* Save Button with Dropdown */}
          <div className="relative flex-shrink-0" ref={saveDropdownRef}>
            <div className="flex">
              <Button
                data-testid="save-order-button"
                disabled={cartItemsCount === 0 || loading}
                onClick={() => handleSaveOrder(false)}
                className="h-10 sm:h-11 md:h-12 px-4 sm:px-5 md:px-6 bg-primary text-white hover:bg-primary/90 rounded-l-full text-xs sm:text-sm md:text-base font-bold uppercase tracking-wider"
              >
                {loading ? '...' : 'Save'}
              </Button>
              
              <Button
                data-testid="save-dropdown-toggle"
                disabled={cartItemsCount === 0 || loading}
                onClick={() => setShowSaveDropdown(!showSaveDropdown)}
                className="h-10 sm:h-11 md:h-12 px-2 bg-primary text-white hover:bg-primary/90 rounded-r-full border-l border-white/20"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showSaveDropdown ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {showSaveDropdown && (
              <div className="absolute bottom-full right-0 mb-2 w-44 sm:w-48 bg-surface rounded-xl shadow-lg border border-border overflow-hidden z-50">
                <button
                  data-testid="save-only-option"
                  onClick={() => handleSaveOrder(false)}
                  disabled={loading}
                  className="w-full px-4 py-3 text-left hover:bg-muted flex items-center gap-2 text-primary font-medium text-sm"
                >
                  Save Only
                </button>
                <button
                  data-testid="save-and-print-option"
                  onClick={() => handleSaveOrder(true)}
                  disabled={loading}
                  className="w-full px-4 py-3 text-left hover:bg-muted flex items-center gap-2 text-primary font-medium border-t border-border text-sm"
                >
                  <Printer className="h-4 w-4" />
                  Save & Print
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Printer Settings Modal */}
      <PrinterSettings
        isOpen={showPrinterSettings}
        onClose={() => {
          setShowPrinterSettings(false);
          fetchPrinterSettings();
        }}
        franchiseId={user.franchise.id}
      />

      {/* Unified Payment Selection Modal */}
      <Dialog open={showPaymentModal} onOpenChange={(open) => {
        setShowPaymentModal(open);
        if (!open) setPartySearchQuery('');
      }}>
        <DialogContent className="bg-surface max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-bold">Select Payment</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto mt-4 space-y-4">
            {/* Payment Methods Section */}
            <div className="space-y-2">
              <p className="text-xs sm:text-sm font-semibold text-primary px-1">Payment Method</p>
              <div className="grid grid-cols-2 gap-2">
                {PAYMENT_METHODS.map((method) => {
                  const Icon = method.icon;
                  const isSelected = paymentMethod === method.id && !selectedPartyId;
                  
                  return (
                    <Card
                      key={method.id}
                      data-testid={`payment-method-${method.id}`}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        isSelected ? 'border-2 border-accent ring-2 ring-accent/20' : 'border border-border'
                      }`}
                      onClick={() => handleSelectPayment(method.id)}
                    >
                      <CardContent className="p-3 flex items-center gap-2">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[method.color]}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-primary text-sm truncate">{method.name}</p>
                          <p className="text-[10px] text-secondary truncate">{method.description}</p>
                        </div>
                        {isSelected && (
                          <div className="h-5 w-5 rounded-full bg-accent flex items-center justify-center ml-auto flex-shrink-0">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Other Payment Method Input */}
              {paymentMethod === 'other' && !selectedPartyId && (
                <div className="space-y-2 pt-2">
                  <Input
                    type="text"
                    placeholder="Enter payment method name..."
                    value={paymentMethodOther}
                    onChange={(e) => setPaymentMethodOther(e.target.value)}
                    className="h-10 rounded-lg border-2 focus:border-accent"
                    autoFocus
                  />
                  <Button
                    onClick={handleConfirmOtherPayment}
                    disabled={!paymentMethodOther.trim()}
                    className="w-full h-10 bg-primary text-white hover:bg-primary/90 rounded-full font-bold"
                  >
                    Confirm
                  </Button>
                </div>
              )}
            </div>

            {/* Ledger Parties Section */}
            {parties.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-border">
                <p className="text-xs sm:text-sm font-semibold text-primary px-1 flex items-center gap-2">
                  <BookUser className="h-4 w-4" />
                  Party Ledgers
                </p>
                
                {/* Party Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search party..."
                    value={partySearchQuery}
                    onChange={(e) => setPartySearchQuery(e.target.value)}
                    className="pl-9 h-9 rounded-lg text-sm"
                  />
                  {partySearchQuery && (
                    <button
                      onClick={() => setPartySearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {parties
                    .filter(party => {
                      if (!partySearchQuery.trim()) return true;
                      const query = partySearchQuery.toLowerCase();
                      return (
                        (party.party_name || '').toLowerCase().includes(query) ||
                        (party.phone_number || '').toLowerCase().includes(query)
                      );
                    })
                    .map((party) => {
                      const isSelected = selectedPartyId === party.party_id;
                      return (
                        <Card
                          key={party.party_id}
                          data-testid={`ledger-party-${party.party_id}`}
                          className={`cursor-pointer transition-all hover:shadow-md ${
                            isSelected ? 'border-2 border-amber-400 ring-2 ring-amber-200 bg-amber-50' : 'border border-border'
                          }`}
                          onClick={() => handleSelectPayment(paymentMethod, party)}
                        >
                          <CardContent className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                <BookUser className="h-4 w-4 text-amber-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold text-primary text-sm truncate">{party.party_name}</p>
                                <p className="text-[10px] text-secondary">{party.phone_number}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <p className={`text-xs font-bold ${
                                parseFloat(party.balance) >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                ₹{parseFloat(party.balance).toFixed(0)}
                              </p>
                              {isSelected && (
                                <div className="h-5 w-5 rounded-full bg-amber-500 flex items-center justify-center">
                                  <span className="text-white text-xs">✓</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  {parties.filter(party => {
                    if (!partySearchQuery.trim()) return true;
                    const query = partySearchQuery.toLowerCase();
                    return (
                      (party.party_name || '').toLowerCase().includes(query) ||
                      (party.phone_number || '').toLowerCase().includes(query)
                    );
                  }).length === 0 && (
                    <p className="text-xs text-secondary text-center py-3">No parties found</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GenerateOrder;
